import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibraryCoachPostSessionParams } from '../../../services/api/dataLibrary';
import type {
  DataLibraryCoachPostSessionRow,
  DataLibrarySurveyQuestion,
  DataLibraryCoachPostSessionFilterOptions,
  ColumnAverages,
} from '../../../types';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibraryCoachPostSessionProps {
  selectedCohortIds: string[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

const STATIC_COLUMNS: ColumnConfig[] = [
  { id: 'cohort_name', label: 'Cohort', visible: true, sortable: false },
  { id: 'coach_name', label: 'Coach Name', visible: true, sortable: true },
  { id: 'group_name', label: 'Group', visible: true, sortable: false },
  { id: 'session_number', label: 'Session Number', visible: true, sortable: true },
  { id: 'session_topic', label: 'Session Topic', visible: true, sortable: false },
  { id: 'session_date', label: 'Session Date', visible: true, sortable: true },
  { id: 'attendance', label: 'Attendance', visible: true, sortable: false, groupable: false },
  // MDF-212: the session's attendance rate (Sessions tab scope) and mean engagement rating.
  { id: 'attendance_rate', label: 'Attendance %', visible: true, sortable: false, groupable: false, exportKey: 'attendance_rate', numeric: 'percent' },
  { id: 'absence_reasons', label: 'Reason', visible: true, sortable: false, groupable: false },
  { id: 'coach_notes', label: 'Coach Notes', visible: true, sortable: false, groupable: false },
  { id: 'engagement', label: 'Participant Engagement', visible: true, sortable: false, groupable: false },
  { id: 'avg_engagement', label: 'Avg Engagement', visible: true, sortable: false, groupable: false, exportKey: 'avg_engagement', numeric: 'decimal' },
  { id: 'concern_flags', label: 'Participant Concern Flags', visible: true, sortable: false, groupable: false },
  { id: 'date_submitted', label: 'Date Submitted', visible: true, sortable: true },
];

export function useDataLibraryCoachPostSession({
  selectedCohortIds,
  activeGroup,
}: UseDataLibraryCoachPostSessionProps) {
  const [rows, setRows] = useState<DataLibraryCoachPostSessionRow[]>([]);
  const [questions, setQuestions] = useState<DataLibrarySurveyQuestion[]>([]);
  // MED-1296 (AC4): start in the loading state so the table shows its loader on
  // mount rather than flashing the empty message for the frame before the fetch
  // effect runs. The tab only renders once cohorts are selected (guarded by both
  // DataLibraryLayout and the tab itself), so this never strands a loader on the
  // no-cohorts state; the early return below also resolves it defensively.
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  // MDF-41: server averages over all matching rows, by column id
  const [averages, setAverages] = useState<ColumnAverages>({});
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibraryCoachPostSessionFilterOptions>({
    groups: [],
    cohorts: [],
    coaches: [],
    sessionNumbers: [],
  });
  const [hasSurveyConfig, setHasSurveyConfig] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort — default session_number ASC
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'session_number',
    order: 'ASC',
  });

  // Filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>([]);

  // Quick filters
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);

  // Column config
  const defaultColumns: ColumnConfig[] = useMemo(() => {
    // MED-888: use short_label as the column header when provided (partner-coach
    // questions), keeping the full question text available for hover via the
    // tooltip column header rendered by CoachPostSessionTable.
    const questionCols: ColumnConfig[] = questions.map(q => ({
      id: `q_${q.question_id}`,
      label: q.short_label || q.question_text,
      visible: true,
      sortable: false,
      groupable: false,
    }));

    return [...STATIC_COLUMNS, ...questionCols];
  }, [questions]);

  // MDF-209: saved order and visibility for this tab
  const { columns, setColumns } = useDataLibraryColumns(defaultColumns);

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params
  const buildParams = useCallback((): DataLibraryCoachPostSessionParams => {
    const params: DataLibraryCoachPostSessionParams = {
      cohortIds: selectedCohortIds,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
      missingFeedback: activeQuickFilters.includes('missing_feedback'),
      hasFlags: activeQuickFilters.includes('has_flags'),
    };

    if (debouncedSearch) params.search = debouncedSearch;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'group') params.groupId = filter.values.join(',');
      if (filter.key === 'coach') params.coachId = filter.values[0];
      if (filter.key === 'session_number') params.sessionNumber = filter.values.join(',');
    }

    return params;
  }, [selectedCohortIds, page, pageSize, sortConfig, debouncedSearch, activeFilters, activeQuickFilters]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setRows([]);
      setQuestions([]);
      setTotalCount(0);
      setAverages({});
      setTotalPages(0);
      setLoading(false); // MED-1296 (AC4): resolve the initial loading state when there is nothing to fetch.
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getCoachPostSessionFeedback({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getCoachPostSessionFeedback(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setRows(result.data || []);
      setQuestions(result.meta.questions || []);
      setTotalCount(result.meta.pagination.total);
      setAverages(result.meta.averages);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
      setHasSurveyConfig(result.meta.hasSurveyConfig);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load coach post-session feedback',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters, activeQuickFilters, selectedCohortIds]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Filter handlers
  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const handleQuickFilterToggle = useCallback((key: string) => {
    setActiveQuickFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setActiveQuickFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeFilters.some(f => f.values.length > 0) ||
    activeQuickFilters.length > 0 ||
    debouncedSearch.length > 0;

  // Client-side grouping — default by Coach Name
  const groupedRows = useMemo(() => {
    return groupDataByLevels(
      rows as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibraryCoachPostSessionRow>[] | null;
  }, [rows, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    activeQuickFilters,
    handleQuickFilterToggle,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
  };
}
